import type { OpenAiSecretStatus } from "../types/workspace";

interface SecretStatusResponse {
  ok: boolean;
  workspaceId: string;
  openai: OpenAiSecretStatus;
  error?: string;
}

interface SecretMutationResponse {
  ok: boolean;
  workspaceId: string;
  openai: OpenAiSecretStatus;
  error?: string;
}

interface ParsedResponse<T> {
  data: T | null;
  text: string;
}

async function parseResponse<T>(response: Response): Promise<ParsedResponse<T>> {
  const text = await response.text();
  if (!text) {
    return { data: null, text: "" };
  }
  try {
    return { data: JSON.parse(text) as T, text };
  } catch {
    return { data: null, text };
  }
}

function handleError(
  response: Response,
  payload: { error?: string } | null,
  fallback: string,
  rawText?: string,
): never {
  const message = payload?.error ?? rawText ?? `${fallback} (status ${response.status})`;
  throw new Error(message);
}

export async function fetchWorkspaceSecretStatus(workspaceId: string): Promise<OpenAiSecretStatus> {
  const response = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/secrets/status`);
  const { data: payload, text } = await parseResponse<SecretStatusResponse>(response);

  if (!response.ok || !payload || payload.ok === false) {
    handleError(response, payload, "Failed to load workspace secrets", text);
  }

  return payload.openai;
}

export async function saveWorkspaceOpenAiSecret(workspaceId: string, value: string): Promise<OpenAiSecretStatus> {
  const response = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/secrets/openai`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value }),
  });
  const { data: payload, text } = await parseResponse<SecretMutationResponse>(response);

  if (!response.ok || !payload || payload.ok === false) {
    handleError(response, payload, "Failed to save OpenAI API key", text);
  }

  return payload.openai;
}

export async function clearWorkspaceOpenAiSecret(workspaceId: string): Promise<OpenAiSecretStatus> {
  const response = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/secrets/openai`, {
    method: "DELETE",
  });
  const { data: payload, text } = await parseResponse<SecretMutationResponse>(response);

  if (!response.ok || !payload || payload.ok === false) {
    handleError(response, payload, "Failed to clear OpenAI API key", text);
  }

  return payload.openai;
}
